/**
 * Tailwind/CSS implementation of the Checkbox component.
 * Uses the Seeds checkbox CSS classes from checkbox.css.
 *
 * Renders the default appearance only: a styled wrapper around `@base-ui/react`'s
 * Checkbox: `Checkbox.Root` renders a `<button role="checkbox">` (the box) plus
 * a hidden `<input>` for form participation, and `Checkbox.Indicator` holds the
 * real `<Icon>` mark. base-ui owns `checked` / `indeterminate` / `disabled` as
 * first-class state props, so there is no imperative `indeterminate` sync and no
 * mask-image pseudo-element. The public API (`TypeCheckboxProps`) and every
 * `data-qa` attribute are preserved unchanged so consumers need no changes. The
 * pill appearance is unsupported on this path — `CheckboxHybrid` routes
 * `appearance="pill"` to the legacy styled-components component.
 */

import * as React from "react";
import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import Icon from "@sproutsocial/seeds-react-icon";
import type { TypeCheckboxProps } from "./types/CheckboxTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const CheckboxTailwind = React.forwardRef<
  HTMLInputElement,
  TypeCheckboxProps
>((props, ref) => {
  const {
    id,
    value,
    name,
    label,
    labelProps,
    checked,
    disabled = false,
    indeterminate = false,
    onChange,
    ariaLabel,
    // Destructured only to strip it from `...rest` (so it is never spread onto
    // the wrapper DOM element). `appearance="pill"` is routed to the legacy
    // styled-components component by `CheckboxHybrid`, so this path only ever
    // renders the default appearance.
    appearance,
    qa = {},
    tabIndex,
    inputProps = {},
    className,
    ...rest
  } = props;

  // @ts-notes - tabIndex requires a number but can be a string
  const tabIndexInt =
    typeof tabIndex === "string" ? parseInt(tabIndex, 10) : tabIndex;

  const Wrapper = (label ? "label" : "span") as React.ElementType;

  // Shared data-qa contract — identical names/values to the legacy
  // styled-components path. `data-qa-checkbox-ischecked` is the tri-state string
  // contract: "indeterminate" | true | false.
  const qaProps = {
    "data-qa-checkbox": id,
    "data-qa-checkbox-ischecked": indeterminate
      ? "indeterminate"
      : checked === true,
    "data-qa-checkbox-isdisabled": disabled === true,
  };

  // Gate `indeterminate` on `checked` so an unchecked-but-indeterminate box
  // renders empty (identical to a plain unchecked box) rather than showing the
  // dash. This matches the styled-components spec, where the mark's opacity is
  // `checked ? 1 : 0` regardless of `indeterminate` (styles.tsx:224). Truth
  // table: checked+indeterminate → dash, checked → check, anything unchecked →
  // empty. The raw `indeterminate` prop still drives `qaProps` so the
  // `data-qa-checkbox-ischecked="indeterminate"` contract is unchanged.
  const effectiveIndeterminate = checked === true && indeterminate;
  // Bridge the public `onChange(SyntheticEvent)` API onto base-ui's
  // #FUTURE CLEANUP - Ideally we'd just lean into base-ui's API, but that is a large breaking change.
  // For now, this is some code I hate, but keeping it to transform base-ui's event into synthetic event
  const handleCheckedChange = (next: boolean, eventOrDetails: unknown) => {
    // No `onChange` → nothing to forward. The control stays fully controlled
    // via the `checked`/`indeterminate` props passed to `Checkbox.Root`, so a
    // click on a controlled box still snaps back to the prop.
    if (!onChange) return;

    const nativeEvent =
      eventOrDetails instanceof Event
        ? eventOrDetails
        : (eventOrDetails as { event?: Event } | null | undefined)?.event;

    // Prefer the real hidden <input> as the event target when base-ui fired
    // from it (its `.checked`/`.value` already satisfy the contract); otherwise
    // synthesize a native-input-shaped target from `next`.
    const nativeTarget = nativeEvent?.target as unknown as
      | HTMLInputElement
      | undefined;

    const target =
      nativeTarget && typeof nativeTarget.checked === "boolean"
        ? nativeTarget
        : {
            checked: next,
            value: value ?? "",
            name: name ?? "",
            id,
            type: "checkbox",
          };

    // Always deliver a SyntheticEvent shape with a real `.nativeEvent`, so the
    // `e.nativeEvent`-reading contract (e.g. shiftKey) is preserved on both the
    // hidden-input and synthesized paths.
    onChange({
      nativeEvent,
      target,
      currentTarget: target,
      type: nativeEvent?.type ?? "change",
      preventDefault: () => nativeEvent?.preventDefault?.(),
      stopPropagation: () => nativeEvent?.stopPropagation?.(),
    } as unknown as React.SyntheticEvent<HTMLInputElement>);
  };

  return (
    <Wrapper className={cn("seeds-checkbox", className)} {...rest}>
      <BaseCheckbox.Root
        id={id}
        checked={checked}
        indeterminate={effectiveIndeterminate}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
        // Forwarded ref targets the hidden <input> so the HTMLInputElement
        // contract is preserved.
        inputRef={ref ?? undefined}
        className="seeds-checkbox-input"
        aria-label={ariaLabel}
        tabIndex={tabIndexInt}
        // `name`/`value` feed base-ui's hidden form <input>. Spread (rather
        // than as explicit attributes) so they pass through regardless of
        // base-ui's exact prop typing.
        {...{ name, value }}
        {...qaProps}
        {...qa}
        // `inputProps` is typed for a native <input>; base-ui forwards unknown
        // props to the rendered <button>. Spread last so consumers can still
        // override computed attributes (e.g. aria-label).
        {...(inputProps as unknown as React.ComponentProps<
          typeof BaseCheckbox.Root
        >)}
      >
        {/* keepMounted so the mark is always in the DOM; CSS toggles its
            visibility from base-ui's aria-checked state (true | mixed). */}
        <BaseCheckbox.Indicator
          className="seeds-checkbox-indicator"
          keepMounted
        >
          <Icon
            className="seeds-checkbox-icon"
            name={effectiveIndeterminate ? "minus-solid" : "check-solid"}
            size="mini"
            fixedWidth
          />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      {label && (
        <span
          className="seeds-checkbox-label"
          {...(labelProps as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {label}
        </span>
      )}
    </Wrapper>
  );
});

CheckboxTailwind.displayName = "CheckboxTailwind";
